import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowDown, faArrowUp, faBolt, faCalendarCheck, faClock, faCoins, faMessage, faMinus, faRobot, faSearch, faSync, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import CommerceActivity from '../../components/CommerceActivity';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { SlidingPills } from '../../components/ui/SlidingPills';
import StatusNotice from '../../components/ui/StatusNotice';
import DefaultCard from '../../components/ui/DefaultCard';
import { useResolvedTheme } from '../../components/ui/useResolvedTheme';
import { useGetGeneralStatsQuery, useGetStatsQuery, type GeneralStatItem } from '../../store/bots/botsApi';
import { clinicsService, type ClinicChatStats } from '../../services/clinicsService';
import './stats-dashboard.css';

type StatsTab = 'chat-bots' | 'clinic-chats' | 'activity';
type SortMode = 'messages' | 'appointments' | 'tokens' | 'name' | 'recent';
const number = (value: number) => new Intl.NumberFormat('es').format(value);
const dateOptions = [
  { value: 'today', label: 'Hoy' },
  { value: 'last_7_days', label: 'Últimos 7 días' },
  { value: 'last_30_days', label: 'Últimos 30 días' },
  { value: 'this_month', label: 'Este mes' },
  { value: 'all', label: 'Siempre' },
];

function tabFromHash(hash: string): StatsTab | undefined {
  if (hash === '#activity') return 'activity';
  if (hash === '#clinic-chats' || hash === '#chats') return 'clinic-chats';
  if (hash === '#chat-bots' || hash === '#agentes') return 'chat-bots';
}

function StatCard({ title, value, icon, subtitle }: { title: string; value: string | number; icon: IconDefinition; subtitle?: string }) {
  return (
    <DefaultCard className="stats-metric">
      <div className="stats-metric-label"><FontAwesomeIcon icon={icon} aria-hidden="true" /><span>{title}</span></div>
      <strong className="stats-metric-value">{typeof value === 'number' ? number(value) : value}</strong>
      {subtitle && <span className="stats-caption">{subtitle}</span>}
    </DefaultCard>
  );
}

function GeneralStatBlock({ title, stat, icon }: { title: string; stat?: GeneralStatItem | null; icon: IconDefinition }) {
  const change = stat?.percentage_change ?? null;
  return (
    <DefaultCard as="article" className="stats-overview-card">
      <div className="stats-metric-label"><span className="stats-icon"><FontAwesomeIcon icon={icon} aria-hidden="true" /></span><span>{title}</span></div>
      <div className="stats-overview-value"><strong>{number(stat?.current ?? 0)}</strong>
        {change !== null && <span className="stats-trend" data-direction={change > 0 ? 'up' : change < 0 ? 'down' : 'flat'} aria-label={`${change > 0 ? 'Aumento' : change < 0 ? 'Descenso' : 'Sin cambio'}: ${Math.abs(change)}%`}>
          <FontAwesomeIcon icon={change > 0 ? faArrowUp : change < 0 ? faArrowDown : faMinus} aria-hidden="true" />{Math.abs(change)}%
        </span>}
      </div>
      <p className="stats-caption">{change !== null ? `Período anterior: ${number(stat?.previous ?? 0)}` : 'Total del período seleccionado'}</p>
    </DefaultCard>
  );
}

export default function StatsDashboardPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useResolvedTheme();
  const [activeTab, setActiveTab] = useState<StatsTab>(() => tabFromHash(window.location.hash) ?? (location.pathname.includes('clinic-chats') ? 'clinic-chats' : 'chat-bots'));
  const [dateRange, setDateRange] = useState('last_30_days');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('messages');
  const [chatStats, setChatStats] = useState<ClinicChatStats | null>(null);
  const [isLoadingChatStats, setIsLoadingChatStats] = useState(false);
  const [chatError, setChatError] = useState('');
  const chatRequest = useRef(0);

  useEffect(() => {
    const handleHashChange = () => {
      const tab = tabFromHash(window.location.hash);
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleTabChange = (tab: string) => {
    const next = tab as StatsTab;
    setActiveTab(next);
    window.history.replaceState(null, '', `#${next}`);
  };

  const { data: botStats = [], isLoading: isLoadingBotStats, isError: botError, refetch: refetchBotStats, isFetching: isFetchingBotStats } = useGetStatsQuery({ time_range: dateRange });
  const { data: generalStats, isError: generalError, refetch: refetchGeneral, isFetching: isFetchingGeneral } = useGetGeneralStatsQuery({ time_range: dateRange });

  const fetchChatStats = useCallback(async () => {
    const request = ++chatRequest.current;
    setIsLoadingChatStats(true);
    setChatError('');
    try {
      const data = await clinicsService.getChatStats(dateRange);
      if (request === chatRequest.current) setChatStats(data);
    } catch (error) {
      if (request === chatRequest.current) setChatError('No se pudieron cargar las estadísticas de chats. Pulsa Actualizar para reintentar.');
      console.error('Failed to load chat stats', error);
    } finally {
      if (request === chatRequest.current) setIsLoadingChatStats(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (activeTab === 'clinic-chats') void fetchChatStats();
    return () => { chatRequest.current += 1; };
  }, [activeTab, fetchChatStats]);

  const handleRefresh = () => {
    if (activeTab === 'chat-bots') { void refetchBotStats(); void refetchGeneral(); }
    else void fetchChatStats();
  };

  const filteredStats = useMemo(() => {
    if (!Array.isArray(botStats)) return [];
    const term = searchTerm.toLowerCase().trim();
    return botStats.filter(bot => (bot.name || '').toLowerCase().includes(term) || String(bot.bot_id || '').includes(term)).sort((a, b) => {
      if (sortMode === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortMode === 'appointments') return (b.appointments_booked || 0) - (a.appointments_booked || 0);
      if (sortMode === 'tokens') return (b.total_tokens || 0) - (a.total_tokens || 0);
      if (sortMode === 'recent') return String(b.last_used_at || '').localeCompare(String(a.last_used_at || ''));
      return (b.messages_sent || 0) + (b.messages_received || 0) - (a.messages_sent || 0) - (a.messages_received || 0);
    });
  }, [botStats, searchTerm, sortMode]);
  const isFetchingCurrent = activeTab === 'chat-bots' ? isFetchingBotStats || isFetchingGeneral : isLoadingChatStats;
  const periodLabel = dateOptions.find(option => option.value === dateRange)?.label;

  return (
    <div data-ui="stats-page" data-theme={theme} className="stats-dashboard page-theme-surface page-theme-stats custom-scrollbar">
      <div className="stats-content">
        <header className="stats-header">
          <div className="stats-heading">
            <span className="stats-eyebrow">ANÁLISIS DEL COMERCIO</span>
            <h1>{t('stats.module_title', 'Estadísticas del comercio')}</h1>
            <p>Entiende tu actividad y el rendimiento de tus conversaciones.</p>
          </div>
          {activeTab !== 'activity' && <div className="stats-period-controls" aria-label="Controles de estadísticas">
            <div className="stats-period-select"><span className="stats-field-label">Período</span><GhostSelect ariaLabel="Per?odo de estad?sticas" value={dateRange} onChange={setDateRange} options={dateOptions} /></div>
            <BtnTaratic onClick={handleRefresh} disabled={isFetchingCurrent} className="stats-refresh"><FontAwesomeIcon icon={faSync} className={isFetchingCurrent ? 'animate-spin' : ''} aria-hidden="true" /><span>{t('common.refresh', 'Actualizar')}</span></BtnTaratic>
          </div>}
        </header>

        <nav className="stats-tabs" aria-label="Secciones de estadísticas">
          <SlidingPills options={[
            { id: 'activity', label: t('stats.activity.title', 'Actividad del comercio'), icon: faClock },
            { id: 'chat-bots', label: t('stats.bots.title', 'Agentes IA'), icon: faRobot },
            { id: 'clinic-chats', label: t('stats.clinic_chats.title', 'Chats de pacientes'), icon: faMessage },
          ]} activeId={activeTab} onChange={handleTabChange} variant={theme} tabMinWidth="0px" size="md" />
        </nav>

        {activeTab === 'activity' && <CommerceActivity />}

        {activeTab === 'chat-bots' && <div className="stats-stack" aria-busy={isFetchingCurrent}>
          {(botError || generalError) && <StatusNotice variant="danger" role="alert">No se pudieron cargar todas las estadísticas. Pulsa Actualizar para reintentar.</StatusNotice>}
          {generalStats && <section className="stats-overview" aria-label="Resumen de agentes">
            <GeneralStatBlock title="Mensajes gestionados" stat={generalStats.total_messages_handled} icon={faMessage} />
            <GeneralStatBlock title="Citas agendadas" stat={generalStats.appointments_booked} icon={faCalendarCheck} />
            <GeneralStatBlock title="Pacientes que interactuaron" stat={generalStats.unique_users_interacted} icon={faUsers} />
          </section>}

          <DefaultCard as="section" className="stats-panel stats-agents">
            <div className="stats-section-header">
              <div><h2>Rendimiento por agente <span className="stats-count">{filteredStats.length}</span></h2><p className="stats-caption">Mensajes, citas y consumo · {periodLabel}</p></div>
              <div className="stats-agent-filters">
                <label className="stats-search"><FontAwesomeIcon icon={faSearch} aria-hidden="true" /><input type="search" aria-label="Buscar agente por nombre o ID" placeholder="Buscar agente…" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} /></label>
                <div className="stats-sort" aria-label="Orden de los agentes"><GhostSelect ariaLabel="Orden de los agentes" value={sortMode} onChange={value => setSortMode(value as SortMode)} options={[
                  { value: 'messages', label: 'Más mensajes' }, { value: 'appointments', label: 'Más citas' }, { value: 'tokens', label: 'Más tokens' }, { value: 'name', label: 'Nombre' }, { value: 'recent', label: 'Última actividad' },
                ]} /></div>
              </div>
            </div>

            {isLoadingBotStats ? <div className="stats-empty" role="status"><FontAwesomeIcon icon={faSync} className="animate-spin" aria-hidden="true" /><p>Cargando estadísticas…</p></div>
              : !filteredStats.length ? <div className="stats-empty"><span className="stats-empty-icon"><FontAwesomeIcon icon={faRobot} aria-hidden="true" /></span><h3>{botStats.length ? 'Ningún agente coincide' : 'Todavía no hay actividad'}</h3><p>{botStats.length ? 'Prueba con otro nombre o identificador.' : 'Las métricas aparecerán cuando tus agentes gestionen conversaciones.'}</p>{searchTerm && <BtnTaratic onClick={() => setSearchTerm('')}>Limpiar búsqueda</BtnTaratic>}</div>
              : <div className="stats-agent-list">{filteredStats.map(bot => <DefaultCard as="article" className="stats-agent" key={bot.bot_id}>
                <div className="stats-agent-heading"><span className="stats-icon"><FontAwesomeIcon icon={faRobot} aria-hidden="true" /></span><div><h3>{bot.name}</h3><p className="stats-caption">Agente #{bot.bot_id}</p></div></div>
                <div className="stats-agent-metrics">
                  <StatCard title={t('stats.bots.messagesHandled', 'Mensajes gestionados')} value={bot.messages_sent + bot.messages_received} subtitle={`${number(bot.messages_received)} recibidos · ${number(bot.messages_sent)} enviados`} icon={faMessage} />
                  <StatCard title={t('stats.bots.avgResponseTime', 'Respuesta media')} value={bot.avg_response_time_ms ? `${(bot.avg_response_time_ms / 1000).toFixed(2)} s` : '—'} icon={faBolt} />
                  <StatCard title={t('stats.bots.uniqueUsers', 'Pacientes únicos')} value={bot.unique_users} icon={faUsers} />
                  <StatCard title={t('stats.bots.appointmentsBooked', 'Citas agendadas')} value={bot.appointments_booked} icon={faCalendarCheck} />
                  <StatCard title={t('stats.bots.tokensUsed', 'Tokens utilizados')} value={bot.total_tokens || 0} subtitle={`Coste: $${(bot.total_cost_usd || 0).toFixed(4)}`} icon={faCoins} />
                  <StatCard title={t('stats.bots.lastUsed', 'Última interacción')} value={bot.last_used_at ? new Date(bot.last_used_at).toLocaleDateString() : '—'} subtitle={bot.last_used_at ? new Date(bot.last_used_at).toLocaleTimeString() : 'Sin interacciones registradas'} icon={faClock} />
                </div>
              </DefaultCard>)}</div>}
          </DefaultCard>
        </div>}

        {activeTab === 'clinic-chats' && <div className="stats-stack" aria-busy={isLoadingChatStats}>
          {chatError && <StatusNotice variant="danger" role="alert">{chatError}</StatusNotice>}
          {isLoadingChatStats && !chatStats ? <DefaultCard className="stats-panel stats-empty" role="status"><FontAwesomeIcon icon={faSync} className="animate-spin" aria-hidden="true" /><p>Cargando estadísticas de chats…</p></DefaultCard>
            : !chatStats ? <DefaultCard className="stats-panel stats-empty"><span className="stats-empty-icon"><FontAwesomeIcon icon={faMessage} aria-hidden="true" /></span><h2>No hay estadísticas de chats disponibles</h2><p>La actividad de tus pacientes aparecerá aquí.</p></DefaultCard>
            : <>
              <section className="stats-clinic-overview" aria-label="Resumen de chats">
                <StatCard title="Mensajes totales" value={chatStats.total_messages ?? 0} subtitle={`${number(chatStats.total_user_messages ?? 0)} de pacientes · ${number(chatStats.total_assistant_messages ?? 0)} de operadores`} icon={faMessage} />
                <StatCard title="Conversaciones activas" value={chatStats.active_threads ?? 0} subtitle={`De ${number(chatStats.total_threads ?? 0)} conversaciones`} icon={faBolt} />
                <StatCard title="Mensajes por paciente" value={chatStats.avg_messages_per_patient ?? 0} subtitle="Media en el período seleccionado" icon={faUsers} />
                <StatCard title="Respuesta media" value={`${((chatStats.avg_operator_response_time_ms ?? 0) / 60000).toFixed(1)} min`} subtitle="Tiempo de respuesta del operador" icon={faClock} />
              </section>
              <div className="stats-chart-grid">
                <DefaultCard as="section" className="stats-panel stats-chart-panel"><div className="stats-section-header"><div><h2>Actividad de mensajes</h2><p className="stats-caption">{periodLabel}</p></div><span className="stats-chart-key"><span />Mensajes</span></div>
                  {!chatStats.timeline?.length ? <div className="stats-empty"><p>No hay mensajes en este período.</p></div> : <div className="stats-chart" role="img" aria-label="Evolución del número de mensajes por fecha">
                    <ResponsiveContainer width="100%" height="100%"><AreaChart data={chatStats.timeline} margin={{ top: 12, right: 12, left: -16, bottom: 0 }}>
                      <defs><linearGradient id="statsMessageFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0.01} /></linearGradient></defs>
                      <XAxis dataKey="date" stroke="var(--stats-muted)" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} /><YAxis stroke="var(--stats-muted)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <CartesianGrid strokeDasharray="3 5" stroke="var(--stats-border)" vertical={false} />
                      <Tooltip contentStyle={{ backgroundColor: 'var(--stats-surface)', color: 'var(--stats-text)', border: '1px solid var(--stats-border)', borderRadius: 12 }} itemStyle={{ color: 'var(--stats-accent)' }} />
                      <Area type="monotone" dataKey="messages" name="Mensajes" stroke="#3b82f6" strokeWidth={2.5} fill="url(#statsMessageFill)" isAnimationActive={false} />
                    </AreaChart></ResponsiveContainer>
                  </div>}
                </DefaultCard>
                <DefaultCard as="section" className="stats-panel stats-patients"><div className="stats-section-header"><div><h2>Pacientes más activos</h2><p className="stats-caption">Por número de mensajes</p></div></div>
                  {chatStats.top_patients?.length ? <ol className="stats-patient-list">{chatStats.top_patients.map((patient, index) => <li key={patient.patient_id}><span className="stats-rank">{index + 1}</span><span className="stats-patient-name">{patient.full_name}</span><span className="stats-patient-count">{number(patient.message_count)} <span>mensajes</span></span></li>)}</ol> : <div className="stats-empty"><p>No hay pacientes activos en este período.</p></div>}
                </DefaultCard>
              </div>
            </>}
        </div>}
      </div>
    </div>
  );
}
